import { useMemo, useState } from "react";
import { useEffect } from "react";
import { usePeopleTagsContext } from "../PeopleTagsContext";
import { useTranslation } from "react-i18next";
import { useToastContext } from "@ttc3k/trekker";
import _ from "lodash";
import { PersonDetailsDocument } from "gql/generated";
import { useApolloClient } from "@apollo/client";
import { usePostHog } from "shared/hooks";

export const usePeopleTagsManageModal = (
  isOpen: boolean,
  onClose: () => void,
) => {
  const { t } = useTranslation("people", {
    keyPrefix: "HEADER.TAGS.MODAL",
  });
  const { t: tCore } = useTranslation("core");
  const { toastFactory } = useToastContext();
  const { trackPostHogException } = usePostHog();

  const { allTagGroupsWithTags, createTagLoading, createTagError, createTag } =
    usePeopleTagsContext();

  const [isCreatingTag, setIsCreatingTag] = useState(false);
  const [newTagValue, setNewTagValue] = useState("");
  const [currentLabel, setCurrentLabel] = useState("");

  // If there are NO tags, start in the adding state
  useEffect(() => {
    setIsCreatingTag(allTagGroupsWithTags[0]?.tags.length === 0 ? true : false);
  }, [allTagGroupsWithTags, isOpen]);

  const handleClose = () => {
    setNewTagValue("");
    setIsCreatingTag(false);
    onClose();
  };

  // Save the new tag
  const handleSave = async () => {
    setCurrentLabel(newTagValue);
    const tag = await createTag(newTagValue);
    if (tag) {
      toastFactory.create({
        title: tCore("SUCCESS"),
        description: t("SUCCESS", { label: newTagValue }),
      });
    } else {
      trackPostHogException(new Error("Error creating tag"), {
        fileName: "usePeopleTagsManageModal.ts",
        method: "handleSave",
        message: "Error creating tag",
      });
      toastFactory.create({
        title: tCore("ERROR.TITLE"),
        description: t("ERROR", { label: newTagValue }),
      });
    }

    // Clean up
    setCurrentLabel("");
    setNewTagValue("");
    setIsCreatingTag(false);
  };

  // If there is an error adding or deleting a tag, show a toast
  useEffect(() => {
    if (!createTagError) return;

    const description = createTagError
      ? t("ERROR", { label: currentLabel })
      : t("DELETE_MODAL.ERROR", { label: currentLabel });
    trackPostHogException(new Error("Error creating tag"), {
      fileName: "usePeopleTagsManageModal.ts",
      method: "handleSave",
      message: "Error creating tag",
    });
    toastFactory.create({
      title: tCore("ERROR.TITLE"),
      description,
    });
  }, [
    currentLabel,
    t,
    tCore,
    toastFactory,
    createTagError,
    trackPostHogException,
  ]);

  // Add the PersonDetailsDocument to the refreshQueries IF the person details modal is open
  const client = useApolloClient();
  const isPersonDetailsModalOpen = Array.from(
    client.getObservableQueries().values(),
  ).some(item => item.queryName === "PersonDetails");

  const refreshQueries = useMemo(
    () => (isPersonDetailsModalOpen ? [PersonDetailsDocument] : []),
    [isPersonDetailsModalOpen],
  );

  return {
    isSaving: createTagLoading,
    isCreatingTag,
    setIsCreatingTag,
    newTagValue,
    setNewTagValue,
    currentLabel,
    handleSave,
    handleClose,
    refreshQueries,
  };
};
